/* eslint-disable max-lines */
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  Button,
  Checkbox,
  Chip,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import articlesService from "services/articlesService";
import { typesTailles, allTaillesMap } from "constants/Tailles";
import type CreateArticleModel from "models/CreateArticleModel";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router";
import NumberInput from "components/atoms/inputs/NumberInput";
import type Article from "models/Article";
import type PatchData from "models/PatchData";
import PhotoInput from "components/atoms/inputs/PhotoInput";
import { routesConfig } from "config/app-config";
import PageTitle from "components/molecules/PageTitle";
import categories from "constants/Categories";
import type { Metier } from "models";
import metiersService from "services/metiersService";
import { sortMetier } from "utils/metierUtil";
import ToastMessages from "constants/ToastMessages";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

type CreationProps = {
  creation: boolean;
  modification?: never;
  article?: never;
};
type ModificationProps = {
  creation?: never;
  modification: boolean;
  article: Article;
};

type ArticleFormProps = CreationProps | ModificationProps;

function ArticleForm({ creation, modification, article }: Readonly<ArticleFormProps>): ReactNode {
  function isValueIfModificationElseDefault<T>(value: T | undefined, defaultValue: T): T {
    let newValue: T | undefined;
    if (modification != null && modification && value != null) {
      newValue = value;
    }
    return newValue ?? defaultValue;
  }
  const [metiers, setMetiers] = useState<Array<Metier>>([]);
  useEffect(() => {
    async function getMetiers(): Promise<void> {
      const _metiers = await metiersService.getAll();
      setMetiers(sortMetier(_metiers));
    }
    void getMetiers();
  }, []);

  const [selectedLabel, setSelectedLabel] = useState<string>(
    isValueIfModificationElseDefault(article?.label, "")
  );
  const [selectedCategorie, setSelectedCategorie] = useState<string>(
    isValueIfModificationElseDefault(article?.categorie, "")
  );
  const [selectedMetiersIds, setSelectedMetiersIds] = useState<Array<string>>(
    isValueIfModificationElseDefault(
      article?.metiers?.map((m) => m.id),
      []
    )
  );
  const [selectedUniteMesureTaille, setSelectedUniteMesureTaille] = useState<string>(
    isValueIfModificationElseDefault(article?.uniteMesureTaille, "")
  );
  const [selectedMinimum, setSelectedMinimum] = useState<string>(
    isValueIfModificationElseDefault(article?.tailles[0], "")
  );
  const [selectedMaximum, setSelectedMaximum] = useState<string>(
    isValueIfModificationElseDefault(article?.tailles[article?.tailles.length - 1], "")
  );
  const [selectedDescription, setSelectedDescription] = useState<string>(
    isValueIfModificationElseDefault(article?.description, "")
  );
  const [quantiteMax, setQuantiteMax] = useState(
    isValueIfModificationElseDefault(article?.quantiteMax, 0)
  );
  const [quantiteMaxParPeriode, setQuantiteMaxParPeriode] = useState(
    isValueIfModificationElseDefault(article?.quantiteMaxParPeriode, 0)
  );
  const [quantiteMaxPeriode, setQuantiteMaxPeriode] = useState(
    isValueIfModificationElseDefault(article?.quantiteMaxPeriode, 0)
  );

  const [protection, setProtection] = useState(
    // eslint-disable-next-line @typescript-eslint/dot-notation
    isValueIfModificationElseDefault(article?.informations["Protection"], "")
  );
  const [habilitation, setHabilitation] = useState(
    // eslint-disable-next-line @typescript-eslint/dot-notation
    isValueIfModificationElseDefault(article?.informations["Habilitation"], "")
  );
  const [dureeDeVie, setDureeDeVie] = useState(
    isValueIfModificationElseDefault(article?.informations["Durée de vie"], "")
  );
  const [composition, setComposition] = useState(
    // eslint-disable-next-line @typescript-eslint/dot-notation
    isValueIfModificationElseDefault(article?.informations["Composition"], "")
  );
  const [entretien, setEntretien] = useState(
    // eslint-disable-next-line @typescript-eslint/dot-notation
    isValueIfModificationElseDefault(article?.informations["Entretien"], "")
  );
  const [caracteristiques, setCaracteristiques] = useState(
    isValueIfModificationElseDefault(article?.informations["Caractéristiques"], "")
  );

  const [imageBase64Data, setImageBase64Data] = useState("");
  const [imageIsUriRemoved, setImageIsUriRemoved] = useState(false);

  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const TAILLE_UNIQUE_STRING = "Taille unique";
  const isTailleUnique = selectedUniteMesureTaille === TAILLE_UNIQUE_STRING;

  useEffect(() => {
    if (isInitialized || (creation != null && creation)) {
      setSelectedMinimum("");
      setSelectedMaximum("");
    }
    if (!isInitialized) setIsInitialized(true);
    // Cas à la marge où c'est OK d'ignorer la règle ESLint, l'idée de _reset_
    // la présélection des tailles min et max lorsque la catégorie de taille est modifiée.
    // Ajouter `isInitialized` et `creation` dépendances aura un effet de bord
    // supplémentaire: au changement de valeur de `isInitialized` (2e render), le code de
    // reset sera effectué, et la conséquence directe est la perte de la présélection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUniteMesureTaille]);

  function isMaxError(): boolean {
    const currentTailles = allTaillesMap.get(selectedUniteMesureTaille);
    return (
      selectedMinimum !== "" &&
      selectedMaximum !== "" &&
      !isTailleUnique &&
      currentTailles != null &&
      (currentTailles.indexOf(selectedMinimum) ?? 0) >
        (currentTailles.indexOf(selectedMaximum) ?? 0)
    );
  }

  function isFormReadyToSend(): boolean {
    return (
      !isMaxError() &&
      selectedLabel !== "" &&
      selectedCategorie !== "" &&
      selectedUniteMesureTaille !== "" &&
      (isTailleUnique || (selectedMinimum !== "" && selectedMaximum !== ""))
    );
  }

  function getCurrentSelectedTaillesSubArray(): Array<string> {
    if (isTailleUnique) {
      return [TAILLE_UNIQUE_STRING];
    }

    const currentTailles = allTaillesMap.get(selectedUniteMesureTaille);
    if (currentTailles == null) {
      throw new Error("mauvaises tailles");
    }

    const indexMin = currentTailles.indexOf(selectedMinimum);
    const indexMax = currentTailles.indexOf(selectedMaximum) + 1;
    const subTailles = currentTailles.slice(indexMin, indexMax);

    return subTailles;
  }

  function getCurrentSelectedMetiers(): Array<Metier> | undefined {
    const selectedMetiers = selectedMetiersIds.reduce<Array<Metier>>((acc, m) => {
      const metier = metiers.find((metier) => metier.id === m);
      if (metier != null) {
        acc.push(metier);
      }
      return acc;
    }, []);
    return selectedMetiers.length > 0 ? selectedMetiers : undefined;
  }

  function onImageBase64Removed(): void {
    setImageBase64Data("");
  }

  function onImageNewBase64(base64: string): void {
    setImageBase64Data(base64);
  }

  function onImageUriRemoved(): void {
    setImageIsUriRemoved(true);
  }

  function onMetiersChange(event: SelectChangeEvent<Array<string>>): void {
    let selection = event.target.value as Array<string>;
    if (metiers != null) {
      selection = metiers.filter((m) => selection.includes(m.id)).map((m) => m.id);
    }
    setSelectedMetiersIds(selection);
  }

  async function sendNewArticle(): Promise<void> {
    setIsLoading(true);
    try {
      const articleToCreate: CreateArticleModel = {
        label: selectedLabel,
        categorie: selectedCategorie,
        metiers: getCurrentSelectedMetiers(),
        uniteMesureTaille: selectedUniteMesureTaille,
        tailles: getCurrentSelectedTaillesSubArray(),
        description: selectedDescription,
        base64Photo: imageBase64Data ?? "",
        quantiteMax,
        quantiteMaxParPeriode,
        quantiteMaxPeriode,
        informations: {},
      };

      const informationsAndLabels: Record<string, string> = {
        Protection: protection,
        Habilitation: habilitation,
        "Durée de vie": dureeDeVie,
        Composition: composition,
        Entretien: entretien,
        Caractéristiques: caracteristiques,
      };

      for (const key in informationsAndLabels) {
        if (informationsAndLabels[key] != null && informationsAndLabels[key] !== "") {
          articleToCreate.informations[key] = informationsAndLabels[key];
        }
      }

      const createdArticle = await articlesService.create(articleToCreate);
      if (createdArticle?.id != null) {
        enqueueSnackbar(ToastMessages.CREATION_ARTICLE_SUCCESS, { variant: "success" });
        navigate(routesConfig.catalogArticle.getParameterPath(createdArticle.id));
      } else {
        throw new Error("error creating article ! response is undefined");
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(ToastMessages.CREATION_ARTICLE_ERROR, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  }

  async function sendModifiedArticle(): Promise<void> {
    if (article == null) {
      return;
    }
    setIsLoading(true);
    try {
      const stringPropertiesToCheck: Record<
        string,
        string | number | Array<string> | Record<string, string> | Array<Metier> | undefined
      > = {
        label: selectedLabel,
        categorie: selectedCategorie,
        metiers: getCurrentSelectedMetiers(),
        uniteMesureTaille: selectedUniteMesureTaille,
        description: selectedDescription,
        quantiteMax,
        quantiteMaxParPeriode,
        quantiteMaxPeriode,
      };

      const patchData: Array<PatchData> = [];

      // #region Primitive type properties
      for (const key in stringPropertiesToCheck) {
        if (
          Object.hasOwn(stringPropertiesToCheck, key) &&
          Object.hasOwn(article, key) &&
          article[key as keyof Article] !== stringPropertiesToCheck[key]
        ) {
          patchData.push({
            op: "replace",
            // Cette syntaxe garantie une valeur `string` même si la clé est par exemple un `number`
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-template-expression
            path: `${key}`,
            value: stringPropertiesToCheck[key],
          });
        }
      }
      // #endregion

      const taillesPatchData = getTaillesPatchData();
      if (taillesPatchData != null) {
        patchData.push(taillesPatchData);
      }

      const informationsPatchData = getInformationsPatchData();
      if (informationsPatchData != null) {
        patchData.push(informationsPatchData);
      }

      const photoPatchData = getPhotoPatchData();
      if (photoPatchData != null) {
        patchData.push(photoPatchData);
      }

      if (patchData.length !== 0) {
        await articlesService.patch(article.id, patchData);

        enqueueSnackbar(ToastMessages.MODIFICATION_ARTICLE_SUCCESS, { variant: "success" });
        navigate(routesConfig.catalog.path);
      } else {
        enqueueSnackbar(ToastMessages.NO_CHANGES, { variant: "warning" });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(ToastMessages.MODIFICATION_ARTICLE_ERROR, { variant: "error" });
    }

    setIsLoading(false);
  }

  function getTaillesPatchData(): PatchData | undefined {
    const tempTailles = getCurrentSelectedTaillesSubArray();
    if (
      (article?.tailles.length === tempTailles.length &&
        tempTailles.some((v, i) => v !== article.tailles[i])) ||
      article?.tailles.length !== tempTailles.length
    ) {
      return {
        op: "replace",
        path: "tailles",
        value: tempTailles,
      };
    }
  }

  function getInformationsPatchData(): PatchData | undefined {
    if (article == null) return;

    const tempInformations: Record<string, string> = {};
    const informationsAndLabels: Record<string, string> = {
      Protection: protection,
      Habilitation: habilitation,
      "Durée de vie": dureeDeVie,
      Composition: composition,
      Entretien: entretien,
      Caractéristiques: caracteristiques,
    };

    for (const key in informationsAndLabels) {
      if (informationsAndLabels[key] != null && informationsAndLabels[key] !== "") {
        tempInformations[key] = informationsAndLabels[key];
      }
    }

    if (
      (Object.keys(tempInformations).length === Object.keys(article.informations).length &&
        Object.keys(tempInformations).some(
          (v) => tempInformations[v] !== article.informations[v]
        )) ||
      Object.keys(tempInformations).length !== Object.keys(article.informations).length
    ) {
      return {
        op: "replace",
        path: "informations",
        value: tempInformations,
      };
    }
  }

  function getPhotoPatchData(): PatchData | undefined {
    if (article?.photo.sasUri != null && article.photo.sasUri !== "") {
      // Si on avait une ancienne photo et qu'il y en a une nouvelle
      if (imageBase64Data != null && imageBase64Data !== "") {
        return {
          op: "replace",
          path: "/Photo",
          value: {
            Base64: imageBase64Data,
          },
        };
      }
      // Si on avait une ancienne photo et qu'on la enlevée,
      // et qu'il n'y en a pas de nouvelle
      else if (imageIsUriRemoved) {
        return {
          op: "remove",
          path: "/Photo",
        };
      }
    }
    // Si on n'avait pas d'ancienne photo, et qu'on en a une nouvelle
    else if (imageBase64Data != null && imageBase64Data !== "") {
      return {
        op: "add",
        path: "/Photo/Base64",
        value: imageBase64Data,
      };
    }
  }

  return (
    <>
      <PageTitle
        title={
          modification != null && modification ? "Modification article" : "Ajout nouvel article"
        }
      />
      <Container maxWidth="lg">
        <Grid container spacing={2} columnSpacing={4} size={12} sx={{ alignItems: "center" }}>
          <Grid container size={12}>
            <TextField
              fullWidth
              label="Titre de l'article"
              required
              value={selectedLabel}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                setSelectedLabel(e.target.value);
              }}
            />
          </Grid>

          <Grid container size={{ xs: 12, sm: 5 }} sx={{ justifyContent: "center" }}>
            <Grid container size={12} sx={{ width: 270 }}>
              <PhotoInput
                defaultImageUri={
                  modification != null && modification ? article?.photo.sasUri : undefined
                }
                onBase64Removed={onImageBase64Removed}
                onNewBase64={onImageNewBase64}
                onUriRemoved={onImageUriRemoved}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} size={{ xs: 12, sm: 7 }}>
            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={selectedCategorie}
                  onChange={(e: SelectChangeEvent<string>) => {
                    setSelectedCategorie(e.target.value);
                  }}
                  label="Catégorie">
                  {categories.map((categorie) => (
                    <MenuItem key={categorie.dataBaseLabel} value={categorie.dataBaseLabel}>
                      {categorie.dataBaseLabel}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Métiers</InputLabel>
                <Select
                  multiple
                  value={selectedMetiersIds}
                  onChange={onMetiersChange}
                  renderValue={(selected) =>
                    selected.map((s) => (
                      <Chip
                        key={s}
                        label={metiers.find((m) => m.id === s)?.libelle ?? s}
                        size="small"
                        sx={{ fontSize: "0.9em", mr: 0.5 }}
                      />
                    ))
                  }
                  label="Métiers">
                  {metiers.map((metier) => (
                    <MenuItem key={metier.id} value={metier.id}>
                      <Checkbox
                        checked={selectedMetiersIds.includes(metier.id)}
                        sx={{ p: 0.5, mr: 1 }}
                      />
                      <ListItemText primary={metier.libelle} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel>Type de taille</InputLabel>
                <Select
                  value={selectedUniteMesureTaille}
                  onChange={(e: SelectChangeEvent<string>) => {
                    setSelectedUniteMesureTaille(e.target.value);
                  }}
                  label="Type de taille">
                  {Object.keys(typesTailles).map((taille) => (
                    <MenuItem key={taille} value={taille}>
                      {taille}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={6}>
              <FormControl
                fullWidth
                required={!isTailleUnique}
                disabled={isTailleUnique || selectedUniteMesureTaille === ""}
                error={isMaxError()}>
                <InputLabel>Taille Min</InputLabel>
                <Select
                  value={selectedMinimum}
                  onChange={(e: SelectChangeEvent<string>) => {
                    setSelectedMinimum(e.target.value);
                  }}
                  label="Taille Min">
                  {allTaillesMap.get(selectedUniteMesureTaille)?.map((taille) => (
                    <MenuItem key={taille} value={taille}>
                      {taille}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={6}>
              <FormControl
                fullWidth
                required={!isTailleUnique}
                disabled={isTailleUnique || selectedUniteMesureTaille === ""}
                error={isMaxError()}>
                <InputLabel>Taille Max</InputLabel>
                <Select
                  value={selectedMaximum}
                  onChange={(e: SelectChangeEvent<string>) => {
                    setSelectedMaximum(e.target.value);
                  }}
                  label="Taille Max">
                  {allTaillesMap.get(selectedUniteMesureTaille)?.map((taille) => (
                    <MenuItem key={taille} value={taille}>
                      {taille}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {isMaxError() && (
              <Grid sx={{ paddingTop: 0 }} size={12}>
                <FormHelperText error>
                  La taille maximale doit être supérieure à la taille minimale
                </FormHelperText>
              </Grid>
            )}
          </Grid>

          <Grid size={12}>
            <Typography variant="h6">Quantité maximale autorisée dans une même commande</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <NumberInput value={quantiteMax} onChange={setQuantiteMax} fullWidth />
          </Grid>

          <Grid size={12}>
            <Typography variant="h6">Quantité maximale autorisée sur une période</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <NumberInput
              label="Qte max"
              value={quantiteMaxParPeriode}
              fullWidth
              onChange={setQuantiteMaxParPeriode}
              minValue={0}
              helperText="Saisir 0 pour ne pas définir"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <NumberInput
              label="Sur la période (mois)"
              value={quantiteMaxPeriode}
              onChange={setQuantiteMaxPeriode}
              fullWidth
              minValue={0}
              helperText="Saisir 0 pour ne pas définir"
            />
          </Grid>

          <Grid size={12}>
            <Typography variant="h6">Description</Typography>
          </Grid>
          <Grid size={12}>
            <TextField
              multiline
              fullWidth
              label="Description"
              value={selectedDescription}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                setSelectedDescription(e.target.value);
              }}
            />
          </Grid>
          <Grid size={12}>
            <Typography variant="h3" color="primary">
              Informations pratiques
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">Protection</Typography>
          </Grid>
          <Grid size={12}>
            <TextField
              multiline
              fullWidth
              label="Protection"
              value={protection}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                setProtection(e.target.value);
              }}
            />
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">Habilitation</Typography>
          </Grid>
          <Grid size={12}>
            <TextField
              multiline
              fullWidth
              label="Habilitation"
              value={habilitation}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                setHabilitation(e.target.value);
              }}
            />
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">Durée de vie</Typography>
          </Grid>
          <Grid size={12}>
            <TextField
              multiline
              fullWidth
              label="Durée de vie"
              value={dureeDeVie}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                setDureeDeVie(e.target.value);
              }}
            />
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">Composition</Typography>
          </Grid>
          <Grid size={12}>
            <TextField
              multiline
              fullWidth
              label="Composition"
              value={composition}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                setComposition(e.target.value);
              }}
            />
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">Entretien</Typography>
          </Grid>
          <Grid size={12}>
            <TextField
              multiline
              fullWidth
              label="Entretien"
              value={entretien}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                setEntretien(e.target.value);
              }}
            />
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">Caractéristiques</Typography>
          </Grid>
          <Grid size={12}>
            <TextField
              multiline
              fullWidth
              label="Caractéristiques"
              value={caracteristiques}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                setCaracteristiques(e.target.value);
              }}
            />
          </Grid>

          <Grid container size={12} spacing={0}>
            <Grid container size={12} sx={{ justifyContent: "flex-end" }}>
              {creation === true && (
                <Button
                  variant="contained"
                  disabled={!isFormReadyToSend()}
                  onClick={() => {
                    void sendNewArticle();
                  }}
                  loading={isLoading}
                  loadingPosition="start"
                  startIcon={<AddIcon />}>
                  Créer un nouvel article
                </Button>
              )}
              {modification === true && (
                <Button
                  variant="contained"
                  disabled={!isFormReadyToSend()}
                  onClick={() => {
                    void sendModifiedArticle();
                  }}
                  loading={isLoading}
                  loadingPosition="start"
                  startIcon={<EditIcon />}>
                  Modifier l'article
                </Button>
              )}
            </Grid>
            <Grid container size={12} sx={{ justifyContent: "flex-end" }}>
              <FormHelperText>
                {creation === true &&
                  "Tous les champs obligatoires (*) doivent être remplis pour créer un nouvel article."}
                {modification === true &&
                  "Tous les champs obligatoires (*) doivent être remplis pour modifier un article."}
              </FormHelperText>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

export default ArticleForm;
